import { getTableViewFromTableQuery } from 'owa-mail-triage-table-utils';
import onInitialTableLoadComplete from './onInitialTableLoadComplete';
import type { PerformanceDatapoint } from 'owa-analytics';
import { lazyLoadSearchTable } from 'owa-mail-execute-search-actions';
import loadInitialGroupTable from 'owa-mail-list-actions/lib/actions/table/loadInitialGroupTable';
import loadInitialMailTable from 'owa-mail-list-actions/lib/actions/table/loadInitialMailTable';
import loadInitialGroupFolderTable from 'owa-mail-list-actions/lib/actions/table/loadInitialGroupFolderTable';
import onLoadInitialRowsSucceeded from 'owa-mail-list-actions/lib/actions/table/onLoadInitialRowsSucceeded';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { ActionSource, MailListItemSelectionSource } from 'owa-mail-store';
import getPersonaPhotoQueue from 'owa-persona/lib/actions/getPersonaPhotoQueue';
import { mutatorAction } from 'satcheljs';
import { listViewStore, TableQuery, TableQueryType } from 'owa-mail-list-store';
import { onAfterTableLoad } from '../helpers/onAfterTableLoad';
import { resetSelection } from 'owa-mail-actions/lib/mailListSelectionActions';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { SubstrateAnswerResponse, SubstrateAnswerRequest } from '@1js/search-hostapp-owa';

/**
 * Load the table from the table query
 * @param tableQuery the table query
 * @param loadTableViewDatapoint - performance datapoint that starts when the user initiates the table load
 * @param actionSource action that initiated the switch folder action
 * @param onAnswerResponseReceived is a callback that is called when single call for answer and serp is enabled, it passes the 3s response to the answers component
 * @param substrateAnswerRequest has the answersEntities for for the single call for answer and serp
 * @param substrateAnswersClientFlights has the client flight list for substrateAnswers
 * @return a promise that resolves when the load table from server has completed
 */
export default function loadTableViewFromTableQuery(
    tableQuery: TableQuery,
    loadTableViewDatapoint?: PerformanceDatapoint,
    actionSource?: ActionSource,
    initialSessionData?: SessionData,
    apolloClientPromise?: Promise<ApolloClient<NormalizedCacheObject>>,
    onAnswerResponseReceived?: (value: SubstrateAnswerResponse) => void,
    substrateAnswerRequest?: SubstrateAnswerRequest,
    substrateAnswersClientFlights?: string
): Promise<void> {
    const oldTableView = getSelectedTableView();
    const newTableView = getTableViewFromTableQuery(tableQuery);
    // Use the reference from the store (http://aka.ms/mobx4)
    tableQuery = newTableView.tableQuery;
    // 1. Set the current selected table in list view store to the new table
    setSelectedTableViewId(newTableView.id);
    // Store selectedRowKeys for old table before clearing the selection
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7034 (50,9): Variable 'selectedRowKeysForOldTable' implicitly has type 'any[]' in some locations where its type cannot be determined.
    // @ts-expect-error
    let selectedRowKeysForOldTable = [];
    // 2. Call onBeforeTableLoad before loading new table
    if (oldTableView) {
        // oldTableView is null when first loading OWA as there is no previously selected table
        selectedRowKeysForOldTable = [...oldTableView.selectedRowKeys.keys()];
        // always clear the getPersonaPhotoQueue when switching the table
        getPersonaPhotoQueue.clear();
        // Always reset/clear the selection on the old table.
        resetSelection(
            oldTableView,
            MailListItemSelectionSource.Reset,
            actionSource == 'FolderNodeClick'
        );
    }
    // 3. Load New Table
    let loadTablePromise: Promise<void>;
    switch (tableQuery.type) {
        case TableQueryType.Search:
            loadTablePromise = lazyLoadSearchTable.importAndExecute(
                newTableView,
                onInitialTableLoadComplete,
                onLoadInitialRowsSucceeded,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (75,17): Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
                // @ts-expect-error
                actionSource,
                loadTableViewDatapoint,
                undefined /*state*/,
                onAnswerResponseReceived,
                substrateAnswerRequest,
                substrateAnswersClientFlights
            );
            break;
        case TableQueryType.Group:
            loadTablePromise = loadInitialGroupTable(
                newTableView,
                onInitialTableLoadComplete,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (90,17): Argument of type 'ActionSource | undefined' is not assignable to parameter of type 'ActionSource'.
                // @ts-expect-error
                actionSource
            );
            break;
        case TableQueryType.GroupFolder:
            loadTablePromise = loadInitialGroupFolderTable(
                newTableView,
                onInitialTableLoadComplete,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (100,17): Argument of type 'ActionSource | undefined' is not assignable to parameter of type 'ActionSource'.
                // @ts-expect-error
                actionSource
            );
            break;
        case TableQueryType.Folder:
            loadTablePromise = loadInitialMailTable(
                newTableView,
                onInitialTableLoadComplete,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (110,17): Argument of type 'ActionSource | undefined' is not assignable to parameter of type 'ActionSource'.
                // @ts-expect-error
                actionSource,
                initialSessionData,
                apolloClientPromise
            );
            break;
        default:
            throw new Error('UnsupportedTableQueryType');
    }
    // 4. Call onAfterTableLoad after loading new table
    if (oldTableView) {
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS7005 (123,26): Variable 'selectedRowKeysForOldTable' implicitly has an 'any[]' type.
        // @ts-expect-error
        onAfterTableLoad(selectedRowKeysForOldTable, oldTableView, newTableView);
    }
    return loadTablePromise;
}

const setSelectedTableViewId = mutatorAction('setSelectedTableViewId', (id: string) => {
    listViewStore.selectedTableViewId = id;
});
